// src/store/slices/centralAuthSlice.ts
// Central Identity authentication state management
//
// Thin wrapper: creates the slice from @levelup/central-auth factory,
// and defines app-scoped selectors.

import { createCentralAuthSlice, parseJwtWorkspaceId } from '@levelup/central-auth';
import type { CentralUser, CentralWorkspace } from '@levelup/central-auth';
import { STORAGE_KEY_PREFIX } from '../../config/centralAuth';

// Re-export shared types for backward compatibility
export type { CentralUser, CentralWorkspace };

// ============================================================
// Create slice from shared factory
// ============================================================

const { reducer, actions, storageKeys } = createCentralAuthSlice({
  storageKeyPrefix: STORAGE_KEY_PREFIX,
});

export default reducer;

// ============================================================
// Action Exports
// ============================================================

export const {
  setCentralCredentials,
  updateCentralAccessToken,
  updateCentralUser,
  updateCentralUserAvatar,
  syncCentralUserFromBoot,
  selectCentralWorkspace,
  centralLogout,
  setCentralInitialized,
} = actions;

// Re-export storage keys for manual cleanup if needed
export { storageKeys as CENTRAL_STORAGE_KEYS };

// ============================================================
// State type (for selectors)
// ============================================================

interface CentralAuthState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null;
  user: CentralUser | null;
  selectedWorkspace: CentralWorkspace | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
}

// ============================================================
// Selectors
// ============================================================

export const selectCentralUser = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.user;

export const selectCentralAccessToken = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.accessToken;

export const selectCentralRefreshToken = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.refreshToken;

export const selectCurrentCentralWorkspace = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.selectedWorkspace;

export const selectCentralWorkspaces = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.user?.workspaces ?? [];

export const selectIsCentralAuthenticated = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.isAuthenticated;

export const selectIsCentralInitialized = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.isInitialized;

export const selectCentralTokenExpiresAt = (state: { centralAuth: CentralAuthState }) =>
  state.centralAuth.expiresAt;

/**
 * Check if Central token needs refresh (expires within 5 minutes).
 */
export const selectCentralNeedsRefresh = (state: { centralAuth: CentralAuthState }) => {
  const { expiresAt, refreshToken } = state.centralAuth;
  if (!expiresAt || !refreshToken) return false;
  return Date.now() > expiresAt - 5 * 60 * 1000;
};

/**
 * Extract workspaceId from the Central JWT token.
 */
export const selectTokenWorkspaceId = (state: { centralAuth: CentralAuthState }): string | null => {
  return parseJwtWorkspaceId(state.centralAuth.accessToken);
};
